import { HStack, Stack } from "@chakra-ui/react"
import { Text } from "@/components"
import { Deal } from "@/types"
import { formatDollar } from "@/utils"

interface Props {
  currentDeal: Deal
  releasedTotal: number
}

const TermChip = ({ label, value }: { label: string; value: string }) => (
  <Stack gap={0}>
    <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="semibold" letterSpacing="wide">
      {label}
    </Text>
    <Text fontSize="sm" fontWeight="semibold" color="gray.800">{value}</Text>
  </Stack>
)

export const DealSummarySection = ({ currentDeal, releasedTotal }: Props) => {
  const outstandingAmount = currentDeal?.outstandingAmount ?? 0
  const pendingAmount = currentDeal?.pendingAmount ?? 0
  const sentLiquidity = releasedTotal

  const activeOffer =
    currentDeal.offers.find((o) => o.isAccepted === 1 && o.isActive === 1) ??
    currentDeal.offers.find((o) => o.isActive === 1) ??
    currentDeal.offers[0] ??
    null

  return (
    <Stack gap={6}>
      {/* Mini deal summary */}
      <Stack gap={1}>
        <Text fontSize="xl" fontWeight="bold" color="gray.800">
          {currentDeal.company.name}
        </Text>
        {activeOffer && (
          <HStack gap={5} flexWrap="wrap" mt={1}>
            <TermChip
              label="Total ESO Fund Deal Size"
              value={formatDollar((activeOffer.exerciseCost ?? 0) + activeOffer.desiredLiquidity)}
            />
            {activeOffer.exerciseCost != null && activeOffer.exerciseCost > 0 && (
              <TermChip
                label="Exercise Payment from ESO Fund"
                value={formatDollar(activeOffer.exerciseCost)}
              />
            )}
            <TermChip
              label="Liquidity Payment from ESO Fund"
              value={formatDollar(activeOffer.desiredLiquidity)}
            />
            <TermChip
              label="Participation Percentage (PP)"
              value={`${(activeOffer.pp * 100).toLocaleString()}%`}
            />
            {activeOffer.lp > 0 && (
              <TermChip
                label="Liquidation Preference (LP)"
                value={formatDollar(activeOffer.lp)}
              />
            )}
          </HStack>
        )}
        {currentDeal.esoId && (
          <Text fontSize="xs" color="gray.400" mt={1}>Deal #{currentDeal.esoId}</Text>
        )}
      </Stack>

      {pendingAmount > 0 && (
        <Stack
          p={5}
          borderWidth="1px"
          borderRadius="lg"
          borderColor="darkBlue.200"
          bg="darkBlue.50"
        >
          <Text fontSize="sm" color="gray.500">
            Total Payment Amount Pending
          </Text>
          <Text fontSize="3xl" fontWeight="extrabold" color="darkBlue.700">
            {formatDollar(pendingAmount)}
          </Text>
        </Stack>
      )}
      {sentLiquidity > 0 && (
        <Stack
          p={5}
          borderWidth="1px"
          borderRadius="lg"
          borderColor="green.200"
          bg="green.50"
        >
          <Text fontSize="sm" color="gray.500">
            Total Paid by ESO Fund
          </Text>
          <Text fontSize="2xl" fontWeight="bold">
            {formatDollar(sentLiquidity)}
          </Text>
        </Stack>
      )}
      {outstandingAmount > 0 && (
        <Stack
          p={5}
          borderWidth="1px"
          borderRadius="lg"
          borderColor="blue.200"
          bg="blue.50"
        >
          <Text fontSize="sm" color="gray.500">
            Outstanding Payments from ESO Fund
          </Text>
          <Text fontSize="2xl" fontWeight="bold" color="blue.700">
            {formatDollar(outstandingAmount)}
          </Text>
        </Stack>
      )}
    </Stack>
  )
}
